import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldAlert, RefreshCw, Loader2, Play, Activity, FileSearch, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: FileSearch, label: 'Coding QA Sweep', active: true },
];

const SEV_PILL = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  none: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const STATUS_PILL = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  dismissed: 'bg-cn-bone text-cn-text-muted border-cn-border',
  rebilled: 'bg-blue-50 text-blue-700 border-blue-200',
};

export default function AdminCodingQaSweep() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState('open');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/rcm/qa-sweep/flags?status=${status}`, getAuthHeader());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load coding QA flags');
    } finally { setLoading(false); }
  }, [getAuthHeader, status]);
  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/rcm/qa-sweep/run`, { limit: 50 }, getAuthHeader());
      toast.success(`Audited ${r.data.audited} claims · ${r.data.new_flags} new flags`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally { setRunning(false); }
  };

  const act = async (flagId, st) => {
    try {
      await axios.patch(`${API}/admin/rcm/qa-sweep/${flagId}`, { status: st }, getAuthHeader());
      toast.success(`Marked ${st}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update flag');
    }
  };

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Post-Billing Coding QA Sweep"
      subtitle="AI re-audit of paid claims. Catches upcoding (refund risk) AND downcoding (missed revenue) so you can rebill or document defense."
      testId="admin-coding-qa-sweep-page"
    >
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="qa-controls">
        <Button onClick={runSweep} disabled={running} className="cn-btn cn-btn-primary !py-2" data-testid="qa-run-sweep">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run sweep on 50 newest paid claims
        </Button>
        <select value={status} onChange={e => setStatus(e.target.value)} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="qa-status-select">
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
          <option value="rebilled">Rebilled</option>
        </select>
        <Button onClick={load} className="cn-btn cn-btn-ghost !py-2" data-testid="qa-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}
      {!loading && data && (
        <>
          <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="qa-kpis">
            <Tile label={`${status} flags`} value={data.count} tone="amber" testid="qa-kpi-count" />
            <Tile label="Total missed revenue (estimate)" value={`$${(data.total_missed_revenue || 0).toLocaleString()}`} tone={(data.total_missed_revenue || 0) > 0 ? 'emerald' : 'red'} testid="qa-kpi-missed" />
          </div>
          <div className="cn-card overflow-hidden" data-testid="qa-flags-card">
            {(data.rows || []).length === 0 ? (
              <div className="p-10 text-center" data-testid="qa-empty">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm text-cn-text-muted">No {status} flags. Run a sweep to find more.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="qa-flags-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">Claim #</th>
                      <th className="px-3 py-3">Payer</th>
                      <th className="px-3 py-3">Original</th>
                      <th className="px-3 py-3">Suggested</th>
                      <th className="px-3 py-3 text-right">$ delta</th>
                      <th className="px-3 py-3">Severity</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {(data.rows || []).slice(0, 100).map((f) => (
                      <tr key={f.id} className="hover:bg-cn-bone-50/40" data-testid={`qa-row-${f.id}`}>
                        <td className="px-5 py-2.5 font-medium text-xs">{f.claim_number || f.claim_id?.slice(0, 8)}</td>
                        <td className="px-3 py-2.5 text-xs text-cn-text-muted">{f.payer || '—'}</td>
                        <td className="px-3 py-2.5 text-xs">{(f.original_codes || []).join(', ')}</td>
                        <td className="px-3 py-2.5 text-xs font-semibold">{(f.suggested_codes || []).join(', ')}</td>
                        <td className={`px-3 py-2.5 text-right text-xs font-semibold ${f.missed_revenue_estimate > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {f.missed_revenue_estimate > 0 ? '+' : ''}${f.missed_revenue_estimate}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${SEV_PILL[f.severity]}`}>{f.severity}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${STATUS_PILL[f.status]}`}>{f.status}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {f.status === 'open' && (
                            <div className="flex gap-1 justify-end">
                              <Button onClick={() => act(f.id, 'rebilled')} className="cn-btn cn-btn-primary !py-1 !px-2 text-[10px]" data-testid={`qa-rebill-${f.id}`}>Rebill</Button>
                              <Button onClick={() => act(f.id, 'dismissed')} className="cn-btn cn-btn-ghost !py-1 !px-2 text-[10px]" data-testid={`qa-dismiss-${f.id}`}>Dismiss</Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function Tile({ label, value, tone = 'neutral', testid }) {
  const cls = { neutral: 'text-cn-text', red: 'text-red-700', emerald: 'text-emerald-700', amber: 'text-amber-700' }[tone];
  return (
    <div className="cn-card p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
      <p className={`font-display font-bold text-2xl ${cls}`}>{value ?? 0}</p>
    </div>
  );
}
